import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Home, Mail, CalendarCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BACKEND_URL, BTN_OUTLINE, BTN_PRIMARY } from '../config/brand';

const ReserverSuccess = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const bookingId = params.get('booking_id');

  const [status, setStatus] = useState('loading');
  const [summary, setSummary] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const confirm = async () => {
      if (!bookingId) {
        setStatus('error');
        return;
      }
      try {
        const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/confirm`, { method: 'POST' });
        if (res.ok) {
          sessionStorage.removeItem('digirobot_lead_id');
          let sent = false;
          const confirmBody = await res.json().catch(() => ({}));
          if (typeof confirmBody.email_sent === 'boolean') sent = confirmBody.email_sent;
          try {
            const bookingRes = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}`);
            if (bookingRes.ok) {
              const booking = await bookingRes.json();
              setSummary(booking);
              if (typeof booking.email_sent === 'boolean') sent = booking.email_sent;
            }
          } catch { /* optionnel */ }
          setEmailSent(sent);
        }
        setStatus(res.ok ? 'ok' : 'error');
      } catch {
        setStatus('error');
      }
    };
    confirm();
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-orange-50/30">
      <Navbar />
      <div className="pt-below-site-header pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <p className="text-center text-[#3a00e0] text-sm font-semibold uppercase tracking-wider mb-4">
            DigiRobot
          </p>
          <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-[#3a00e0] via-[#5b21ff] to-[#fab03a]" aria-hidden />
            <div className="py-11 px-6 sm:px-10 text-center">
              {status === 'loading' && (
                <div className="animate-in fade-in duration-300" role="status" aria-live="polite">
                  <div className="w-16 h-16 rounded-full bg-[rgba(58,0,224,0.08)] flex items-center justify-center mx-auto mb-5">
                    <Loader2 className="h-8 w-8 text-[#3a00e0] animate-spin" aria-hidden />
                  </div>
                  <h1 className="text-xl font-semibold text-slate-900 mb-2">Confirmation en cours…</h1>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Nous validons votre réservation, un instant.
                  </p>
                </div>
              )}

              {status === 'ok' && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-500">
                  <div className="w-[4.5rem] h-[4.5rem] rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 className="h-10 w-10 text-emerald-600" aria-hidden />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 tracking-tight">
                    Réservation confirmée
                  </h1>
                  <p className="text-slate-600 mb-5 leading-relaxed">
                    {summary?.payment_mode === 'offline'
                      ? emailSent
                        ? 'Votre demande est enregistrée. Un email de confirmation vient de partir — le règlement se fera sur place.'
                        : "Votre demande est enregistrée. Le règlement se fera sur place. Notre équipe vous recontacte rapidement."
                      : emailSent
                        ? 'Merci pour votre confiance. Un email de confirmation vient de vous être envoyé avec tous les détails.'
                        : "Votre réservation est enregistrée. Nous n'envoyons pas d'email automatique pour le moment — notre équipe vous recontacte rapidement."}
                  </p>

                  {(summary?.child_name || bookingId) && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50/80 text-left px-4 py-3.5 mb-7 space-y-2">
                      {summary?.child_name && (
                        <div className="flex justify-between gap-3 text-sm">
                          <span className="text-slate-500">Enfant</span>
                          <span className="text-slate-900 font-medium">{summary.child_name}</span>
                        </div>
                      )}
                      {summary?.amount != null && (
                        <div className="flex justify-between gap-3 text-sm">
                          <span className="text-slate-500">Montant</span>
                          <span className="text-slate-900 font-medium tabular-nums">
                            {summary.amount === 0 ? 'Gratuit' : `${summary.amount} €`}
                          </span>
                        </div>
                      )}
                      {bookingId && (
                        <div className="flex justify-between gap-3 text-sm">
                          <span className="text-slate-500">Référence</span>
                          <span className="text-slate-700 font-mono text-xs tracking-wide">
                            {bookingId.slice(0, 8).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button asChild className={`h-11 ${BTN_PRIMARY}`}>
                      <Link to="/"><Home className="h-4 w-4 mr-2" />Retour à l&apos;accueil</Link>
                    </Button>
                    <Button asChild variant="outline" className={`h-11 ${BTN_OUTLINE}`}>
                      <Link to="/contact"><Mail className="h-4 w-4 mr-2" />Nous contacter</Link>
                    </Button>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-500">
                  <div className="w-[4.5rem] h-[4.5rem] rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-5">
                    <XCircle className="h-10 w-10 text-red-500" aria-hidden />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">
                    Un problème est survenu
                  </h1>
                  <p className="text-slate-600 mb-7 leading-relaxed">
                    Nous n&apos;avons pas pu confirmer votre réservation automatiquement.
                    Si un débit a été effectué, contactez-nous — nous vous aiderons rapidement.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button asChild className={`h-11 ${BTN_PRIMARY}`}>
                      <Link to="/reserver"><CalendarCheck className="h-4 w-4 mr-2" />Réessayer</Link>
                    </Button>
                    <Button asChild variant="outline" className={`h-11 ${BTN_OUTLINE}`}>
                      <Link to="/contact"><Mail className="h-4 w-4 mr-2" />Contacter le support</Link>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReserverSuccess;
